import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MuiColorInput, MuiColorInputFormat } from "mui-color-input";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { ComponentEnum } from "src/common/enum";
import { FileInputAdornmentBasic } from "../../file-input";
import { ArrayInput, AutocompleteInput} from "src/components/input";

import { __String } from "typescript";

type ComponentInputProps = {
  editor: any;
  element?: HTMLElement;
  setElement?: any;
};

const TypeField = (props) => {
  const {data, type, required, name, label, additional, onChange, ...rest} = props;

  let componentField = null;

  switch (type) {
    case "color": {
      componentField = (
        <MuiColorInput
          format="hex"
          label={label ?? name}
          key={name}
          name={name}
          value={data?.[name] ?? ""}
          required={required}
          size="small"
          onChange={(value) =>
            onChange(value, name)
          }
          {...rest}
        ></MuiColorInput>
      );
      break;
    }
    case "file": {
      componentField = (
        <FileInputAdornmentBasic
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? ""}
            required={required}
            path="admin/media/common"
            fileTypes={["JPG", "JPEG", "PNG", "GIF", "WEBP", "PDF", "XLS", "XLSX", "DOCX"]}
            multiple={false}
            includeDomain={true}
            onChange={onChange}
            {...rest}
        />
      );
      break;
    }
    case "image": {
      componentField = (
        <FileInputAdornmentBasic
            label={label ?? name}
            key={name}
            name={name}
            value={data?.[name] ?? ""}
            required={required}
            fileTypes={["JPG", "JPEG", "PNG", "GIF", "WEBP", "PDF", "XLS", "XLSX", "DOCX", "PDF"]}
            multiple={false}
            includeDomain={true}
            onChange={onChange}
            {...rest}
        />
      );
      break;
    }
    case "product_slug": {
      componentField = (
        <AutocompleteInput
          resource="admin/products"
          label={label ?? name}
          name={name}
          value={data?.[name] ?? ""}
          required={required}
          onChange={onChange}
          property="slug"
          getOptionLabel={(option) => option.sku || ''}
          {...rest}
        />
      );
      break;
    }
    case "products": {
      componentField = (
        <AutocompleteInput
          resource="admin/products"
          multiple
          label={label ?? name}
          name={name}
          value={data?.[name] ? data?.[name].split(',')  : []}
          required={required}
          onChange={onChange}
          property="id"
          getOptionLabel={(option) => option.sku || ''}
          {...rest}
        />
      );
      break;
    }
    case "product": {
      componentField = (
        <AutocompleteInput
          resource="admin/products"
          label={label ?? name}
          name={name}
          value={data?.[name] ?? ""}
          required={required}
          onChange={onChange}
          property="id"
          getOptionLabel={(option) => option.sku || ''}
          {...rest}
        />
      );
      break;
    }
    case "category": {
      componentField = (
        <AutocompleteInput
          resource="admin/product/categories"
          label={label ?? name}
          name={name}
          value={data?.[name] ?? ""}
          required={required}
          onChange={onChange}
          property="id"
          getOptionLabel={(option) => option.name || ''}
          {...rest}
        />
      );
      break;
    }
    case "array": {
      componentField = (
        <ArrayInput 
          label={label ?? name}
          key={name}
          name={name}
          value={data?.[name] ?? []}
          onChange={onChange}
          {...rest}
        >
            {additional.map((field, index) => {
              return (
                <TypeField 
                  key={index}
                  type={field?.type} 
                  required={field?.required ?? false}
                  name={field.name} 
                  label={field.label}
                  onChange={onChange}
                />
              );
            })}
        </ArrayInput>
      );
      break;
    }
    default: {
      componentField = (
        <TextField
          label={label ?? name}
          key={name}
          name={name}
          value={data?.[name] ?? ""}
          required={required}
          size="small"
          onChange={(e) =>
            onChange(e.target.value, name)
          }
          {...rest}
        ></TextField>
      );
      break;
    }
  }

  return componentField;
};

export const ComponentInput = forwardRef((props: ComponentInputProps, ref) => {
  const { editor, element = null, setElement } = props;

  const [open, setOpen] = useState(false);
  const [data, setData] = useState({});
  const [component, setComponent] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [additionalFields, setAdditionalFields] = useState([]);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);

  const handleRemoveOpen = () => {
    setOpenRemoveDialog(true);
  };

  const handleRemoveClose = () => {
    setOpenRemoveDialog(false);
  };

  useEffect(() => {
    setError("");
    if (element) {
      handleComponentChange(element.getAttribute("component"));
      setData(extractData(element));
    } else {
      setTitle("");
    }
  }, [element]);

  useImperativeHandle(
    ref,
    () => {
      return {
        open() {
          setOpen(true);
        },
      };
    },
    []
  );

  function getComponent(id: string) {
    let component =  ComponentEnum.find((component) => component.id == id);

    return component;
  }

  function isJson(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }  
    return true;
  }

  function removeElement() {
    if (null !== element) {
      element.remove();
    }

    handleRemoveClose();
    handleClose();
  }

  function extractData(element: HTMLElement) {
    let newData = { component: element.getAttribute("component") };
    let value = "";
    for (let attributeName of element.getAttributeNames()) {
      if (attributeName === "component") continue;

      if (attributeName === "styleoverride") {
        let style = JSON.parse(getAttribute(attributeName, element));
        let key;

        for (const styleAttribute in style) {
          key = styleAttribute;
          value = style[styleAttribute];

          if (key && value) {
            key = "style-"+key.trim();
            value = value.trim();

            if (borderPostitions.includes(key)) {
              newData = { ...newData, [key]: false };
            } else {
              newData = { ...newData, [key]: value };
            }
          }
        }
      } else {
        value = getAttribute(attributeName, element);
        if (value) newData = { ...newData, [attributeName]: isJson(value) ? JSON.parse(value) : value };
      }
    }

    return newData;
  }

  const getAttribute = (attributeName: string, element: HTMLElement) => {
    let value = element.getAttribute(attributeName);

    return value;
  };

  const borderPostitions = [
    "style-borderTop",
    "style-borderLeft",
    "style-borderRight",
    "style-borderBottom",
  ];

  const validateRequiredFileds = () => {
    if (!component) {
      setError(`Field component cannot be empty`);
      return false;
    }

    if (!data["id"]) {
      setError(`Field id cannot be empty`);
      return false;
    }

    if (getComponent(component)?.fields !== undefined && getComponent(component)?.fields.length !== 0  ) {
      for (let field of getComponent(component).fields) {
        if (field.required === true && data[field.name] === undefined) {
          setError(`Field "${field.name}" cannot be empty`);
          return false;
        }
      }
    }

    return true;
  };

  const insertComponent = () => {
    if (!validateRequiredFileds()) {
      return;
    }

    let htmlElement = document.createElement('sc');

    htmlElement.setAttribute('component', component);
    htmlElement.setAttribute('class', 'nonedit');
    htmlElement.innerHTML = getComponent(component)?.name ?? component;

    let style = {};

    for (let key in data) {
      if (key.includes("style-")) {
        if (key === "style-background" && /^(?:https?:\/\/)?(?:www\.)?[\w-]+(?:\.[\w-]+)+[\w.,@?^=%&:/~+#-]*$/.test(data[key])) {
          style[key.replace("style-", "")] = `url(${data[key]})`;
        } else if (borderPostitions.includes(key)) {
          if (data[key] === false) {
            style[key.replace("style-", "")] = "none";
          }
        } else {
          style[key.replace("style-", "")] = data[key];
        }
      } else {
        htmlElement.setAttribute(key, typeof data[key] === 'object' && data[key] !== null ? JSON.stringify(data[key]) : data[key]);
      }
    }

    if (Object.keys(style).length > 0) {
      htmlElement.setAttribute('styleoverride', JSON.stringify(style));
    }

    if (editor.current && component) {
      if (null === element) {
        editor.current.execCommand('mceInsertContent', false, htmlElement.outerHTML);
      } else {
        element.parentNode.replaceChild(htmlElement, element);
      }

      handleClose();
    }
  };

  const handleDataChange = (value, type: string) => {
    if (!value) {
      let newData = {...data}
      delete newData[type];
      setData(newData);

      return;
    }

    setData({ ...data, [`${type}`]: value });
  };

  const handleComponentChange = (value) => {
    setComponent(value as string);
    setTitle(getComponent(value)?.name ?? "");
    setAdditionalFields(getComponent(value)?.fields ?? []);
  };

  function handleClose(): void {
    setData({});
    setComponent("");
    setElement(null);
    setTitle("");
    setOpen(false);
  }

  return (
    <Modal open={open} onClose={() => handleClose()}>
      <Box sx={{ ...modalContentStyle, overflowY: "auto", maxHeight: "80vh" }}>
        <Box sx={{ marginBottom: "1.5em" }}>
          <Typography variant="h5" component="h5">
            {title !== "" ? title : "Add Component"}
          </Typography>
          {error ? (
            <Typography component="p" color="red">
              {error}
            </Typography>
          ) : null}
        </Box>

        <Box>
        {element === null ? (
          <Box sx={{ display: "flex", gap: "0.5em", marginBottom: "0.5em" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="component-label">Component</InputLabel>
                <Select
                  labelId="component-label"
                  value={component ? component : ""}
                  label="component"
                  onChange={(e) => handleComponentChange(e.target.value)}
                >
                  {ComponentEnum.map((component) => {
                    return (
                      <MenuItem value={component.id} key={component.id}>
                        {component.name}
                      </MenuItem>
                    );
                  })}
                </Select>{" "}
              </FormControl>
          </Box>
          ) : null}
          { component !== "" &&
          <Box
            display="flex"
            width="100%"
            flexDirection="column"
            sx={{ gap: "2em" }}
          >
            <Box
              display="flex"
            >
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="styles-content"
                  id="styles-header"
                >
                  <Typography component="p" color="black" variant="subtitle2">Styles</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="1em"
                  >                  
                    <Box display="flex" gap="0.5em">
                      <TextField
                        label="Background"
                        name="style-background"
                        size="small"
                        value={ data["style-background"] ?? "" }
                        sx={{ width: "100%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                    </Box>
                    <Box display="flex" gap="0.5em">
                      <TextField
                        label="Width"
                        name="style-width"
                        size="small"
                        value={ data["style-width"] ?? "" }
                        sx={{ width: "50%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Min"
                        name="style-minWidth"
                        size="small"
                        value={ data["style-minWidth"] ?? "" }
                        sx={{ width: "25%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Max"
                        name="style-maxWidth"
                        size="small"
                        value={ data["style-maxWidth"] ?? "" }
                        sx={{ width: "25%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                    </Box>
                    <Box display="flex" gap="0.5em">
                      <TextField
                        label="Height"
                        name="style-height"
                        size="small"
                        value={ data["style-height"] ?? "" }
                        sx={{ width: "50%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Min"
                        name="style-minHeight"
                        size="small"
                        value={ data["style-minHeight"] ?? "" }
                        sx={{ width: "25%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Max"
                        name="style-maxHeight"
                        size="small"
                        value={ data["style-maxHeight"] ?? "" }
                        sx={{ width: "25%" }}
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                    </Box>

                    <Typography component="p" variant="subtitle2">Padding</Typography>
                    <Box display="flex" gap="0.5em">
                      <TextField
                        label="Top"
                        name="style-paddingTop"
                        size="small"
                        value={ data["style-paddingTop"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Left"
                        name="style-paddingLeft"
                        size="small"
                        value={ data["style-paddingLeft"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Bottom"
                        name="style-paddingBottom"
                        size="small"
                        value={ data["style-paddingBottom"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Right"
                        name="style-paddingRight"
                        size="small"
                        value={ data["style-paddingRight"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                    </Box>

                    <Typography component="p" variant="subtitle2">Margin</Typography>
                    <Box display="flex" gap="0.5em">
                    <TextField
                        label="Top"
                        name="style-marginTop"
                        size="small"
                        value={ data["style-marginTop"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Left"
                        name="style-marginLeft"
                        size="small"
                        value={ data["style-marginLeft"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Bottom"
                        name="style-marginBottom"
                        size="small"
                        value={ data["style-marginBottom"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                      <TextField
                        label="Right"
                        name="style-marginRight"
                        size="small"
                        value={ data["style-marginRight"] ?? "" }
                        onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                      ></TextField>
                    </Box>

                    <Typography component="p" variant="subtitle2">Border</Typography>
                    <Box display="flex" flexDirection="column" gap="0.5em">
                      <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="style-borderTop"
                              checked={ data["style-borderTop"] ?? false }
                              onChange={(e) => handleDataChange(e.target.checked, e.target.name) }
                            />
                          }
                          label="Top"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="style-borderLeft"
                              checked={ data["style-borderLeft"] ?? false }
                              onChange={(e) => handleDataChange(e.target.checked, e.target.name) }
                            />
                          }
                          label="Left"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="style-borderBottom"
                              checked={ data["style-borderBottom"] ?? false }
                              onChange={(e) => handleDataChange(e.target.checked, e.target.name) }
                            />
                          }
                          label="Bottom"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="style-borderRight"
                              checked={ data["style-borderRight"] ?? false }
                              onChange={(e) => handleDataChange(e.target.checked, e.target.name) }
                            />
                          }
                          label="Right"
                        />
                      </FormGroup>

                      <Box display="flex" gap="0.5em">
                        <TextField
                          label="Width"
                          size="small"
                          name="style-borderWidth"
                          value={ data["style-borderWidth"] ?? "" }
                          onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                        ></TextField>
                        <FormControl fullWidth size="small">
                          <InputLabel id="style-label">style</InputLabel>
                          <Select
                            labelId="style-label"
                            name="style-borderStyle"
                            value={ data["style-borderStyle"] ?? "" }
                            label="Style"
                            onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                          >
                            <MenuItem value="solid">Solid</MenuItem>
                            <MenuItem value="dashed">Dashed</MenuItem>
                            <MenuItem value="dotted">Dotted</MenuItem>
                          </Select>
                        </FormControl>
                        <MuiColorInput
                          label="Color"
                          name="style-borderColor"
                          format={format}
                          size="small"
                          value={ data["style-borderColor"] ?? "#ffffff" }
                          onChange={(color) => handleDataChange(color, "style-border-color") }
                        />
                        <TextField
                          label="Radius"
                          name="style-borderRadius"
                          size="small"
                          value={ data["style-borderRadius"] ?? "" }
                          onChange={(e) => handleDataChange(e.target.value, e.target.name) }
                        ></TextField>
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gap="1em"
            >
              {additionalFields.map((field, index) => (
                <TypeField 
                  key={index}
                  type={field?.type} 
                  required={field?.required ?? false}
                  name={field.name} 
                  label={field.label} 
                  additional={field.additional}
                  data={data}
                  onChange={handleDataChange}
                />
              ))}
            </Box>
          </Box>
        }
        </Box>
        <Box sx={footerStyle}>
          {element !== null && 
            <Button variant="outlined" color="error" onClick={handleRemoveOpen}>
              Remove
            </Button>
          }
          <Dialog
            open={openRemoveDialog}
            onClose={handleRemoveClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Remove { getComponent(component)?.name } Component
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to remove this component?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRemoveClose}>
                No
              </Button>
              <Button onClick={removeElement} autoFocus>
                Yes
              </Button>
            </DialogActions>
          </Dialog>
          <Button variant="contained" onClick={() => insertComponent()}>
            {element ? "Update" : "Insert"}
          </Button>
          <Button variant="outlined" onClick={() => handleClose()}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
});

const format: MuiColorInputFormat = "hex";

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  columnGap: "10px",
  paddingTop: "20px",
};

const modalContentStyle = {
  position: "absolute" as "absolute",
  top: "25%",
  left: "50%",
  minWidth: "300px",
  width: "500px",
  transform: "translate(-50%, -25%)",
  bgcolor: "white",
  boxShadow: 24,
  borderRadius: 1,
  p: 4,
};
